<script>
	import { goto } from "@roxi/routify";
	import { getData, postFormData, postJsonData } from "../helpers.js";
	import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
	import Navbar from "../components/Navbar.svelte";
	import Footer from "../components/Footer.svelte";

	let errorText = '';
	let successText = '';

	function registerWebauthn(residency=false) {
		let response = getData('webauthn/registration-options', {resident: residency});

		response.then(resp => resp.json())
		.then(optionsJSON => {
				// Pass the options to the authenticator and wait for a response
				return startRegistration({ optionsJSON });
			}).catch(error => {
				// Some basic error handling
				successText = '';
				if (error.name === 'InvalidStateError') {
					errorText = 'Error: Authenticator was probably already registered by user';
				} else {
					errorText = error;
				}
				throw error;
			}).then(attResp => {
				return postJsonData('webauthn/validate-registration', attResp)
			}).then(verificationResp => verificationResp.json())
			.then(verificationJson => {
				if (verificationJson && verificationJson.verified) {
					errorText = '';
					successText = 'Success!';
					getCredentials();
				} else {
					successText = '';
					errorText = `Oh no, something went wrong! Response: ${JSON.stringify(verificationJson)}`;
				}
			}).catch(error => {
				successText = '';
				errorText = error;
			});
	};

	let lastAuthenticatedId = '';
	function authenticateWebauthn(stepup = false) {
		let url = stepup ? 'webauthn/stepup-authentication-options' : 'webauthn/authentication-options';
		let response = getData(url);
		let init_data = {}

		response.then(resp => resp.json())
			.then(resp_json => {
				init_data = resp_json;
				let optionsJSON = JSON.parse(resp_json.webauthn_options);
				// Pass the options to the authenticator and wait for a response
				return startAuthentication({ optionsJSON });
			}).catch(error => {
				// Some basic error handling
				successText = '';
				errorText = error;
				throw error;
			}).then(asseResp => {
				return {'challenge_id': init_data.challenge_id,
						'options': init_data.webauthn_options,
				 		'asseResp': asseResp,
				}
			}).then(respObj => {
				return postJsonData('webauthn/validate-authentication', respObj)
			}).then(verificationResp => verificationResp.json())
			.then(verificationJson => {
				if (verificationJson && verificationJson.verified) {
					errorText = '';
					successText = 'Success!';
					lastAuthenticatedId = verificationJson.credential_id;
					getCredentials();
				} else {
					successText = '';
					errorText = `Oh no, something went wrong! Response: ${JSON.stringify(verificationJson)}`;
				}
			}).catch(error => {
				successText = '';
				errorText = error;
			});
	};

	let webauthnCredentials = [];
	function getCredentials() {
		let response = getData("webauthn/get-credentials");
		response.then(resp => resp.json())
		.then(resp_json => {
			if (resp_json.status === "success") {
				webauthnCredentials = resp_json.data;
			} else {
				successText = '';
				errorText = 'Oh no, something went wrong!'
			}
		});
	}
	getCredentials();

	function updateCredential(cred_id) {
		let response = postJsonData("webauthn/update-credential", webauthnCredentials.find(elem => elem.credential_id === cred_id))
		response.then(resp => resp.json())
		.then(resp_json => {
			if (resp_json.status === "success") {
				errorText = '';
				successText = 'Success!';
				getCredentials();
			} else {
				successText = '';
				errorText = `Oh no, something went wrong! Response: ${resp_json.error}`;
			}
		});
	}

	function deleteCredential(cred_id) {
		let response = postJsonData("webauthn/delete-credential", webauthnCredentials.find(elem => elem.credential_id === cred_id))
		response.then(resp => resp.json())
		.then(resp_json => {
			if (resp_json.status === "success") {
				errorText = '';
				successText = 'Success!';
				getCredentials();
			} else {
				successText = '';
				errorText = `Oh no, something went wrong! Response: ${resp_json.error}`;
			}
		});
	}
</script>

<main class=background>
	<Navbar/>
	<div class="lg:mx-64 md:mx-32 sm:mx-10 mx-2 pb-20">
		<div class="mx-auto mt-10">
			<div class="max-w-xl mx-auto p-16 rounded-md shadow-lg bg-white bg-opacity-50">
				<p class="text-2xl font-semibold text-blue-900 mb-2">WebAuthn Options</p>
				<p class='error-text text-red-600 text-xl mb-4 {errorText === '' ? 'invisible' : ''}'>{errorText}</p>
				<p class='success-text text-green-700 text-xl mb-4 {successText === '' ? 'invisible' : ''}'>{successText}</p>
				<div class="flex flex-col space-y-6">
					<div class="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
						<div class="tooltip-div w-full">
							<button on:click={() => registerWebauthn(false)}
								class="w-full px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
							Register FIDO</button>
							<p class='tooltip'><s>Two Factor</s>Currently Unused</p>
						</div>
						<div class="tooltip-div w-full">
							<button on:click={() => registerWebauthn(true)}
								class="w-full px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
							Register Passkey</button>
							<p class='tooltip'>Single Factor Passkey</p>
						</div>
					</div>
					<button on:click={getCredentials}
						class="w-full px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
					Force List Refresh</button>
					<div>
						<div class="md:hidden font-semibold text-lg">
							{#each webauthnCredentials as data}
								<div class="flex flex-col space-y-2 {data.credential_id === lastAuthenticatedId ? 'bg-green-200' : ''}" id={data.credential_id}>
									<div class="flex justify-between">
										<label for="credential-pretty-name-{data.credential_id}">Nickname</label>
										<p>Sign Count: {data.sign_count}</p>	
									</div>
									<input id="credential-pretty-name-{data.credential_id}" name="credential-pretty-name" class="!pt-1" bind:value={data.credential_pretty_name} readonly={!data.editing}>
									<div class="flex justify-between">
										{#if data.editing}
											<div>
												<button on:click={getCredentials} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-red-500 hover:bg-red-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Cancel</button>
											</div>
											<div>
												<button on:click={() => {updateCredential(data.credential_id)}} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Confirm</button>
											</div>
										{:else if data.deleting}
											<div>
												<button on:click={() => {deleteCredential(data.credential_id)}} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Confirm</button>
											</div>
											<div>
												<button on:click={getCredentials} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-red-500 hover:bg-red-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Cancel</button>
											</div>
										{:else if webauthnCredentials.some((elem) => elem.editing || elem.deleting)}
											<!--Nothing-->
										{:else}
											<div>
												<button on:click={() => {data.editing = true}} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Edit</button>
											</div>
											<div>
												<button on:click={() => {data.deleting = true}} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-red-500 hover:bg-red-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Delete</button>
											</div>
										{/if}
									</div>
								</div>
							{/each}
						</div>
						<table class="hidden md:block border-spacing-2 border-collapse text-center">
							<tbody>
								<tr>
									<th id="nick-col-label">Nickname</th>
									<th id="sign-col-label">Sign Count</th>
									<th></th>
									<th></th>
								</tr>
								{#each webauthnCredentials as data}
									<tr class="{data.credential_id === lastAuthenticatedId ? 'bg-green-200' : ''}" id={data.credential_id}>
										<td>
											<input aria-labelledby="nick-col-label" name='credential-pretty-name' class="!pt-1" bind:value={data.credential_pretty_name} readonly={!data.editing}>
										</td>
										<td aria-labelledby="sign-col-label">{data.sign_count}</td>
										{#if data.editing}
											<td>
												<button on:click={getCredentials} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-red-400 hover:bg-red-600 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Cancel</button>
											</td>
											<td>
												<button on:click={() => {updateCredential(data.credential_id)}} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Confirm</button>
											</td>
										{:else if webauthnCredentials.some((elem) => {elem.editing})}
											<!--Nothing-->
										{:else if data.deleting}
											<td>
												<button on:click={() => {deleteCredential(data.credential_id)}} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Confirm</button>
											</td>
											<td>
												<button on:click={getCredentials} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-red-400 hover:bg-red-600 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Cancel</button>
											</td>
										{:else}
											<td>
												<button on:click={() => {data.editing = true}} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Edit</button>
											</td>
											<td>
												<button on:click={() => {data.deleting = true}} class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-red-400 hover:bg-red-600 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
													Delete</button>
											</td>
										{/if}
									</tr>
								{/each}
							</tbody>
						</table>
					</div>
					<div class="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
						<button on:click={() => authenticateWebauthn(false)}
							class="w-full px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
						Test Clean Login</button>
						<button on:click={() => authenticateWebauthn(true)}
							class="w-full px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
						Test Step Up</button>
					</div>
					<button on:click={() => {if (confirm("Delete all credentials? This is IRREVERSIBLE!")) getData('webauthn/delete-credentials').then(() => {getCredentials()})}}
						class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-red-500 hover:bg-red-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
					Delete all Credentials</button>
					<a class="text-lg font-sans mt-1" href="/user-info-menu">
						<button class="px-4 pb-2.5 pt-1.5 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
						Back</button>
					</a>
				</div>
			</div>
		</div>
		<Footer/>
	</div>
</main>

<style>
	main.background { 
		background-color:theme("colors.gray.100");
	}
	.input-field {
		@apply rounded-md shadow-sm font-thin py-1 px-2;
	}
</style>